import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { showErrorToast, tagsAfterClientChange, tagsEnabled } from '@Helpers';

import { sendErrorEvent } from '@Analytics';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@Components/ui-v2/combobox';
import { TrackedButton } from '@Components/ui-v2/tracked-button';
import { useFetchData } from '@CustomHooks';
import { Claim, Options } from '@Models';
import { bopsApi } from '@Network';
import { AppDispatch, RootState, tableServerRevision } from '@Store';
import Modal from '../ui/modal';
import ModalLoading from '../ui/modal-loading';
import ModalSuccess from '../ui/modal-success';
import MultiSelect from '../ui/multiselect';
import Spinner from '../ui/spinner';

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  fetchUsers: () => void;
  IncrementEvent: (event: string) => void;
  clientValue: Options | undefined;
  clients: Options[];
  email: string;
  currentClient: string;
  currentTags?: string[];
  loggedInEmail?: string;
}

const UserEditModal = ({
  isOpen,
  onClose,
  fetchUsers,
  clientValue,
  IncrementEvent,
  clients,
  email,
  currentClient,
  currentTags = [],
  loggedInEmail,
}: UserModalProps) => {
  const { t } = useTranslation();

  const [openSuccess, setOpenSuccess] = useState(false);
  const [openLoading, setOpenLoading] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [clientSelected, setClientSelected] = useState<string | null>(null);

  const {
    data: clientTags,
    error: errorClientTags,
    isLoading: clientTagsLoading,
    refetch: refetchClientTags,
  } = useFetchData(bopsApi.getClientTags, null, true);

  useEffect(() => {
    // Skipped where tags are disabled: the field is hidden, so there is
    // nothing to populate and no reason to hit the endpoint.
    if (clientSelected && tagsEnabled) {
      refetchClientTags(clientSelected);
    }
  }, [clientSelected]);

  useEffect(() => {
    if (isOpen) {
      setSelectedTags(currentTags);
      setClientSelected(clientValue?.value || null);
      return;
    }
    setSelectedTags([]);
    setClientSelected(null);
  }, [isOpen]);

  const onSubmit = () => {
    setOpenLoading(true);

    if (!clientSelected) showErrorToast(t('User.ErrorNoClientSelected'));
    else
      bopsApi
        .changeAccountUser({
          email,
          newClient: clientSelected,
          oldClient: currentClient,
          currentTags,
          newTags: selectedTags,
        })
        .then(() => {
          onClose();
          setOpenLoading(false);
          setOpenSuccess(true);
          const isSelfEdit = !!loggedInEmail && loggedInEmail.toLowerCase() === email.toLowerCase();
          setTimeout(() => {
            if (isSelfEdit) {
              // Reload so the session picks up the new client
              window.location.reload();
              return;
            }
            IncrementEvent('usersEvent');
            fetchUsers();
          }, 3000);
        })
        .catch((error) => {
          sendErrorEvent(error);
          setOpenLoading(false);
          const errorMessage = t('User.ErrorChangeAccount');
          showErrorToast(t(errorMessage));
        });

    setOpenLoading(false);
  };

  return (
    <>
      <Modal modalOpen={isOpen} closeModal={onClose} title={t('User.TitleEditModal')} closeClickOutside={false}>
        <div className="w-full">
          <section className="flex flex-col gap-4">
            <div className="text-sm mt-4">
              <Combobox
                items={clients}
                itemToStringLabel={(item: Options) => item.label}
                // A deactivated client is no longer in `clients`, so keep
                // showing the user's current assignment instead of an empty
                // field.
                value={clients.find((c) => c.value === clientSelected) ?? clientValue ?? null}
                onValueChange={(item: Options | null) => {
                  setClientSelected(item?.value || null);
                  // The submit sends `newTags` whether or not the field was
                  // rendered, so the helper decides: clear where tags are
                  // enabled, keep them where the field is hidden.
                  // Kept tags follow the user to the new client: the backend
                  // stores them under it. On tag-based deployments (bops) they
                  // would then be matched by name against the new client's
                  // configuration links, so the old client's tags get
                  // evaluated against the new client's links — but that path is
                  // never reached here, because tags are only kept where the
                  // field is hidden (logyca, gs1), and those deployments run a
                  // plan-based masters policy that grants regardless of tags.
                  // So the kept tags are inert: they cannot surface a link, and
                  // hence a Masters tab, the moved user is not entitled to.
                  setSelectedTags(tagsAfterClientChange(selectedTags));
                }}>
                <ComboboxInput placeholder={t('User.Select')} />
                {/*
                  TODO: z-[1000000] only exists to sit above ui/modal's z-[999999].
                  That modal z-index is an old, inflated value. We need to fix the
                  root z-index hierarchy so overlays layer correctly without these
                  magic numbers, then drop this override.
                */}
                <ComboboxContent positionerClassName="z-[1000000]">
                  <ComboboxEmpty>{t('User.Select')}</ComboboxEmpty>
                  <ComboboxList>
                    {(item: Options) => (
                      <ComboboxItem key={item.value} value={item}>
                        {item.label}
                      </ComboboxItem>
                    )}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
            </div>

            {tagsEnabled && clientSelected && (
              <div className="text-sm">
                {clientTags ? (
                  <MultiSelect
                    placeholder={t('User.EditTagsPlaceholder')}
                    emptyText={t('User.EditTagsEmpty')}
                    options={
                      clientTags
                        ? clientTags
                            .filter((tag) => !selectedTags.includes(tag))
                            .map((tag) => ({ label: tag, value: tag }))
                        : []
                    }
                    selected={selectedTags.map((tag) => ({ label: tag, value: tag }))}
                    onChange={(selected) => {
                      setSelectedTags(selected.map((option) => option.value));
                    }}
                  />
                ) : clientTagsLoading ? (
                  <Spinner />
                ) : (
                  errorClientTags && <p className="text-sm text-red-500">{`${t('User.EditTagsLoadingError')}`} </p>
                )}
              </div>
            )}
            <div className="flex gap-2 justify-center mt-4 w-full">
              <TrackedButton
                variant="outline"
                buttonName="EDIT_USER_CANCELED"
                onClick={() => {
                  onClose();
                }}
                className="w-full bg-white"
                isSubmit={false}>
                {t('Global.Canceled')}
              </TrackedButton>
              <TrackedButton buttonName="USER_EDITED" className="w-full" isSubmit={false} onClick={onSubmit}>
                <div className="text-white"> {t('User.Edit')} </div>
              </TrackedButton>
            </div>
          </section>
        </div>
      </Modal>

      <ModalSuccess isOpen={openSuccess} onClose={() => setOpenSuccess(false)}>
        <div className="text-center">
          <span className="text-[18px]"> {t('User.EditSuccess')} </span>
        </div>
      </ModalSuccess>

      <ModalLoading isOpen={openLoading} onClose={() => setOpenLoading(false)}>
        <div className="text-center -mt-[9px]">{t('User.LoadingEdit')}</div>
      </ModalLoading>
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  const { claims } = state.authorization || {};

  return {
    loggedInEmail: claims?.[Claim.Email] || claims?.[Claim.GS1Email] || undefined,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  const { IncrementEvent } = tableServerRevision;

  return {
    IncrementEvent: (event: string) => dispatch(IncrementEvent(event)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(UserEditModal);
